import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { invStatusStyle, methodIcon } from '../data';
import StatCard from '../components/StatCard';
import PageHeader from '../components/PageHeader';
import { invoiceDocBadge, invoiceDocType, invoiceDisplayNumber } from '../utils/invoiceDoc';
import ExportInvoicesModal from './InvoicesExport';

// Payment-method buckets for the filter pills. 'card'/'online' group several
// machine codes so the operator sees one clean pill each.
const METHOD_BUCKETS = {
  cash:   ['cash'],
  card:   ['stripe'],
  bank:   ['bank'],
  online: ['online', 'viva', 'paypal', 'paymentlink'],
};
const METHOD_PILLS = [
  { key: '',       label: 'All',    icon: '📂' },
  { key: 'cash',   label: 'Cash',   icon: '💵' },
  { key: 'card',   label: 'Card',   icon: '💳' },
  { key: 'bank',   label: 'Bank',   icon: '🏦' },
  { key: 'online', label: 'Online', icon: '🌐' },
];
function matchesMethodBucket(invMethod, bucketKey) {
  if (!bucketKey) return true;
  return (METHOD_BUCKETS[bucketKey] || [bucketKey]).includes(invMethod);
}

export default function InvoicesPage() {
  const { invoices, updateInvoiceStatus, openModal, showToast, canEdit, companySettings, rentals, vehicles } = useApp();
  const [tab, setTab]       = useState('all');
  const [search, setSearch] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [showExport, setShowExport] = useState(false);

  const drafts   = invoices.filter(i => i.status === 'draft').length;
  const issued   = invoices.filter(i => i.status === 'issued' || i.status === 'pending').length;
  const paid     = invoices.filter(i => i.status === 'paid').length;
  const overdue  = invoices.filter(i => i.status === 'overdue').length;

  // Counts per method bucket for the pill labels
  const methodCounts = useMemo(() => {
    const c = { '': invoices.length, cash: 0, card: 0, bank: 0, online: 0 };
    invoices.forEach(i => {
      Object.keys(METHOD_BUCKETS).forEach(k => {
        if (matchesMethodBucket(i.method, k)) c[k]++;
      });
    });
    return c;
  }, [invoices]);

  const q = search.toLowerCase();
  const filtered = useMemo(() => invoices.filter(i => {
    if (tab !== 'all' && i.status !== tab) return false;
    if (filterMethod && !matchesMethodBucket(i.method, filterMethod)) return false;
    const matchQ = !q || (i.num || '').toLowerCase().includes(q)
      || (i.customer || '').toLowerCase().includes(q)
      || (i.vehicle || '').toLowerCase().includes(q);
    return matchQ;
  }), [invoices, tab, filterMethod, q]);

  return (
    <div>
      <PageHeader
        title="Pro Formas"
        icon="🧾"
        subtitle="Pro forma documents — drafts, issued, paid, and overdue. Track what's paid, then your accountant issues the official receipts."
        actions={canEdit && canEdit('invoices') && (
          <button className="btn btn-primary" onClick={() => openModal('create-invoice')}>+ New Pro Forma</button>
        )}
      />
      {/* Stats — clickable as filters */}
      <div className="sg sg-4" style={{ marginBottom: 14 }}>
        <StatCard
          label="Drafts" value={drafts} variant="bl"
          desc={tab === 'draft' ? 'Filtering — click to clear' : 'Not yet issued'}
          active={tab === 'draft'}
          onClick={() => setTab(tab === 'draft' ? 'all' : 'draft')}
        />
        <StatCard
          label="Issued" value={issued} variant="am"
          desc={tab === 'issued' ? 'Filtering — click to clear' : 'Awaiting payment'}
          active={tab === 'issued'}
          onClick={() => setTab(tab === 'issued' ? 'all' : 'issued')}
        />
        <StatCard
          label="Paid" value={paid} variant="gr"
          desc={tab === 'paid' ? 'Filtering — click to clear' : 'Completed'}
          active={tab === 'paid'}
          onClick={() => setTab(tab === 'paid' ? 'all' : 'paid')}
        />
        <StatCard
          label="Overdue" value={overdue} variant="ac"
          desc={tab === 'overdue' ? 'Filtering — click to clear' : 'Past due date'}
          active={tab === 'overdue'}
          onClick={() => setTab(tab === 'overdue' ? 'all' : 'overdue')}
        />
      </div>

      {/* Payment-method filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {METHOD_PILLS.map(p => {
          const active = filterMethod === p.key;
          return (
            <button
              key={p.key || 'all'}
              type="button"
              onClick={() => setFilterMethod(p.key)}
              style={{
                padding: '6px 13px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${active ? 'var(--ac)' : 'var(--bd)'}`,
                borderRadius: 20,
                background: active ? 'var(--ac-l)' : 'var(--sur)',
                color: active ? 'var(--ac)' : 'var(--tx2)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>{p.icon} {p.label}</span>
              <span style={{
                fontSize: 10, fontWeight: 700,
                background: active ? 'var(--ac)' : 'var(--sur2)',
                color: active ? '#fff' : 'var(--tx3)',
                borderRadius: 10, padding: '1px 7px', fontFamily: "'DM Mono',monospace",
              }}>{methodCounts[p.key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="tb2">
        <div style={{ display: 'flex', gap: 0, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 7, overflow: 'hidden' }}>
          {[['all','All'],['draft','Drafts'],['issued','Issued'],['paid','Paid'],['overdue','Overdue'],['cancelled','Cancelled']].map(([val, lbl]) => (
            <button key={val} className={`vb${tab === val ? ' active' : ''}`} onClick={() => setTab(val)}>{lbl}</button>
          ))}
        </div>
        <div className="srch">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input type="text" placeholder="Search pro forma, customer, vehicle..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ marginLeft: 'auto' }} />
        <button className="btn btn-ghost btn-sm" onClick={() => setShowExport(true)}>📥 Export for Accountant</button>
        <button className="btn btn-primary" onClick={() => openModal('create-invoice')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Pro Forma
        </button>
      </div>

      {/* Table */}
      <div className="tbl">
        <div className="tbl-hd" style={{ gridTemplateColumns: '.9fr 1.4fr 1.5fr 1fr 1fr 1fr .9fr .7fr .5fr' }}>
          <div className="th">Pro Forma #</div>
          <div className="th">Customer</div>
          <div className="th">Vehicle / Contract</div>
          <div className="th">Issue Date</div>
          <div className="th">Due Date</div>
          <div className="th">Amount</div>
          <div className="th">Method</div>
          <div className="th">Status</div>
          <div className="th"></div>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>No pro formas found</div>
        )}

        {filtered.map(inv => {
          const st = invStatusStyle[inv.status] || {};
          return (
            <div key={inv.id} className="tr" style={{ gridTemplateColumns: '.9fr 1.4fr 1.5fr 1fr 1fr 1fr .9fr .7fr .5fr' }}
              onClick={() => openModal('view-invoice', inv.id)}>
              <div>
                <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", fontWeight: 600, color: inv.num ? 'var(--tx)' : 'var(--tx3)' }}>{inv.num ? invoiceDisplayNumber(inv) : '📝 DRAFT'}</div>
                {inv.status !== 'draft' && (() => {
                  const dt = invoiceDocType(inv);
                  const isReceipt = dt === 'receipt';
                  return (
                    <span style={{
                      fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 4, marginTop: 2,
                      display: 'inline-block', textTransform: 'uppercase', letterSpacing: '.03em',
                      background: isReceipt ? 'var(--gr-l)' : 'var(--am-l)',
                      color: isReceipt ? 'var(--gr)' : 'var(--am)',
                      border: `1px solid ${isReceipt ? 'var(--gr-b)' : 'var(--am-b)'}`,
                    }}>{invoiceDocBadge(inv)}</span>
                  );
                })()}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 500 }}>{inv.customer}</div>
                {inv.customerAddress && <div style={{ fontSize: 9, color: 'var(--tx3)' }}>{inv.customerAddress}</div>}
              </div>
              <div>
                <div style={{ fontSize: 11 }}>{inv.vehicle}</div>
                <div style={{ fontSize: 9, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{inv.contractId}</div>
              </div>
              <div className="dv">{inv.issue || inv.issued || '—'}</div>
              <div className="dv" style={inv.status === 'overdue' ? { color: 'var(--ac)', fontWeight: 500 } : {}}>{inv.due}</div>
              <div className="pv">€{inv.total?.toFixed(2)}</div>
              <div style={{ fontSize: 11 }}>{methodIcon[inv.method] || ''} {inv.method}</div>
              <div>
                <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 10, ...st }}>
                  {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                </span>
              </div>
              <div className="abtns" onClick={e => e.stopPropagation()}>
                <button className="ab ab-st" onClick={() => openModal('view-invoice', inv.id)}>View</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Monthly export for accountant */}
      {showExport && (
        <ExportInvoicesModal
          invoices={invoices}
          companySettings={companySettings}
          rentals={rentals}
          vehicles={vehicles}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}
